import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Checkbox, CheckboxGroup, Popup } from 'cat-ecommerce-alloy';
import styles from '../styles.module.scss';
import { isEmpty } from '@app/utils';
import FacetListExpander from './FacetListExpander';
import { activeFacetsPropTypes } from '@app/components/common/SpecificationFacets/declarations.propTypes';

const FacetList = ({
  facetValues,
  handleChange,
  isModal,
  activeFacets,
  widgetId
}) => {
  const [t] = useTranslation();
  const shouldRenderSubspecification = !isEmpty(facetValues?.[0]?.values);

  // setChecked prop is required in order to control the checkbox state
  const setChecked = () => {};
  const responsiveModal = isModal ? 'desktop' : 'mobile';

  const facetCheckbox = ({ count, facet, label, uom, key, groupLabel }) => {
    const checkboxId = widgetId
      ? `${label}-${facet}-${responsiveModal}-${widgetId}`
      : `${label}-${facet}-${responsiveModal}`;
    const checkboxValue = JSON.stringify({
      facet,
      label,
      uom,
      groupLabel
    });

    return (
      <Checkbox
        checked={activeFacets
          ?.map(({ facet: activeFacet }) => activeFacet)
          .includes(facet)}
        className={styles['facet-checkbox__label']}
        data-testid="facetCheckbox"
        disabled={parseInt(count) === 0}
        // The checkbox component needs a unique id, currently we are rendering the same component
        // in desktop and mobile modal, that's why we need to pass the isModal prop
        // to know if the checkbox is rendered in desktop or mobile version.
        id={checkboxId}
        key={key}
        label={{
          primaryText: label
        }}
        name={label}
        setChecked={setChecked}
        value={checkboxValue}
        variant="extended"
      />
    );
  };

  const renderFacets = facets => (
    <CheckboxGroup
      className={cx('mb-0', { [styles['mobile-facet-checkbox']]: isModal })}
      onChange={handleChange}
    >
      {facets.map(({ count, facet, label, uom, groupLabel }) => {
        if (parseInt(count) === 0) {
          return (
            <Popup
              key={`${label}-${facet}-${responsiveModal}`}
              initiator={facetCheckbox({
                count,
                facet,
                label,
                groupLabel
              })}
              contents={<p>{t('FILTER_UNAVAILABLE')}</p>}
            />
          );
        }
        return facetCheckbox({
          count,
          facet,
          label,
          uom,
          key: `${label}-${facet}-${responsiveModal}`,
          groupLabel
        });
      })}
    </CheckboxGroup>
  );

  const renderSubSpecifications = facets => {
    return facets.map(facet => {
      const { label, values } = facet;
      const shouldDisableLabel = values?.every(
        item => parseInt(item.count) === 0
      );

      return (
        <Fragment key={label}>
          <h5
            className={cx(
              'ps-2 m-0 mt-2 text-sm text-capitalize u-color--DarkGray text-sans-serif fw-bold',
              { [styles['facet-group-title']]: shouldDisableLabel }
            )}
          >
            {label}
          </h5>
          {renderFacets(values)}
        </Fragment>
      );
    });
  };

  const isSubspecificationRender = () =>
    shouldRenderSubspecification
      ? renderSubSpecifications(facetValues)
      : renderFacets(facetValues);

  if (isEmpty(facetValues)) {
    return null;
  }
  return facetValues?.length > 8 ? (
    <FacetListExpander
      facetValues={facetValues}
      renderSubSpecifications={renderSubSpecifications}
      renderFacets={renderFacets}
    />
  ) : (
    <div>{isSubspecificationRender()}</div>
  );
};

FacetList.propTypes = {
  facetValues: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      facet: PropTypes.string,
      label: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          ...activeFacetsPropTypes,
          facetId: PropTypes.string
        })
      )
    })
  ),
  activeFacets: activeFacetsPropTypes,
  handleChange: PropTypes.func,
  isModal: PropTypes.bool
};

export default FacetList;
